/**
 * Wire ControlRegistry messages → Adam parameter addresses via bindings.
 */

import {
  applyBindingMode,
  createBindingRuntimeState,
  mapBindingValue,
  selectBindingValue,
  type ControlBindingSpec,
} from "./controlBindings";
import {
  controlRegistry,
  type ControlRegistry,
} from "./controlRegistry";
import {
  applyParamAddress,
  readParamAddress,
  resolveParamAddress,
  type ApplyParamOptions,
} from "./paramAddresses";

export type AdamControlBinding = {
  /** Adam param path, e.g. `/track/0/mix/volume`. */
  target: string;
  /** Incoming control path + selector + transform (often `/midi/cc/74`). */
  binding: ControlBindingSpec;
};

export type AttachParamBindingOptions = ApplyParamOptions & {
  registry?: ControlRegistry;
};

/**
 * Bind an incoming control path to an Adam parameter address.
 * Returns an unbind function.
 */
export function attachParamBinding(
  entry: AdamControlBinding,
  options: AttachParamBindingOptions = {},
): () => void {
  const target = resolveParamAddress(entry.target);
  if (!target) {
    return () => {};
  }
  const registry = options.registry ?? controlRegistry;
  const mid = target.range[0] + (target.range[1] - target.range[0]) * 0.5;
  const initial = readParamAddress(entry.target, options);
  const runtime = createBindingRuntimeState(
    initial != null ? initial >= mid : false,
  );

  return registry.bind(entry.binding.path, {
    apply(message) {
      const raw = selectBindingValue(entry.binding, message);
      if (raw == null) {
        return;
      }
      const mapped = mapBindingValue(raw, entry.binding, target.range);
      const current = readParamAddress(entry.target, options) ?? target.range[0];
      const next = applyBindingMode(
        entry.binding.mode,
        mapped,
        current,
        target.range,
        runtime,
      );
      applyParamAddress(entry.target, next, options);
    },
  });
}

/** Attach many bindings; returns a single teardown. */
export function attachParamBindings(
  entries: readonly AdamControlBinding[],
  options: AttachParamBindingOptions = {},
): () => void {
  const unbinds = entries.map(entry => attachParamBinding(entry, options));
  return () => {
    for (const unbind of unbinds) {
      unbind();
    }
  };
}
